import { useState, useEffect, useCallback, useMemo, memo, useRef, forwardRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  TextField,
  IconButton,
  FormHelperText,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DollarSpinner } from '../components/DollarSpinner';
import {
  IconPencil as EditIcon,
  IconTrash as DeleteIcon,
  IconDeviceFloppy as SaveIcon,
  IconX as CancelIcon,
  IconDotsVertical as MoreVertIcon,
  IconArchiveOff as UnarchiveIcon,
  IconPlus,
  IconChevronDown
} from '@tabler/icons-react';
import api from '../services/api';
import { useGroup } from '../contexts/GroupContext.jsx';
import { useCanEdit, useIsOwner } from '../components/RoleGate.jsx';
import { usePayees } from '../hooks/usePayees';

import { MaskedCurrency } from '../components/MaskedValue';
import { computeRunningBalances, parseAmountInput } from '../utils/decimal';
import AmountDisplay, { resolveAmountColor } from '../components/AmountDisplay';
import { resolveBudgetIcon } from '../utils/budgetIcon';
import { PageHero, ActionBar, Sheet, HorizonChip } from '../components/ds';
import HorizonSwitchDialog from '../components/HorizonSwitchDialog';
import { horizonOf } from '../utils/budgetsByCategory';
import { invalidateListCache, BUDGETS_LIST_CACHE_KEY } from '../utils/listCacheUtil';
import { iconBoxColors } from '../components/ds/iconCategory';
import { budgetBarSegments } from '../utils/budgetBarSegments';
import { CenteredLoader } from '../components/LoadingIndicator';
import AmountInputCell from '../components/AmountInputCell';
import { requiredOutlineSx } from '../utils/requiredFieldSx';
import { useUiPrefs } from '../contexts/UiPrefsContext';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { usePinnedLine } from '../hooks/usePinnedLine';
import { useOverflowCardSwitch } from '../hooks/useOverflowCardSwitch';
import {
  useTransactionFormValidation,
  TRANSACTION_DATE_MIN,
  transactionDateMax
} from '../hooks/useFormValidation';
import { useToast } from '../components/ToastNotification';
import { useTimezoneAwareDateFormatting } from '../components/TimezoneAwareDateDisplay';
import { getLocalDateString } from '../utils/timezone';
import {
  isValidDateStamp,
  INVALID_DATE_MESSAGE,
  DATE_STAMP_MIN,
  DATE_STAMP_MAX
} from '../utils/dateStamp';
import { compareSameDateDesc } from '../utils/lineSort';
import { notifyTangibleRefresh } from '../utils/tangibleRefresh';

// Reusable action menu component (vertical ellipsis → Edit/Delete)
const ActionMenu = memo(({ onEdit, onDelete, disabled = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} disabled={disabled}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => { setAnchorEl(null); onEdit(); }}>
          <ListItemIcon><EditIcon size={20} /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setAnchorEl(null); onDelete(); }}>
          <ListItemIcon><Box component="span" sx={{ color: 'error.main', display: 'inline-flex' }}><DeleteIcon size={20} /></Box></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
});

// #1477: a budget line's source-ledger name links to that ledger, styled like the
// PageHero back link (palette.interactive). ledgerId is projected on the
// budget-date-index GSI, so it is available on the client.
const LedgerLink = ({ ledgerId, name }) => {
  const navigate = useNavigate();
  const { palette } = useTheme();
  return (
    <Box
      component="button"
      type="button"
      onClick={() => navigate(`/ledgers/${ledgerId}`)}
      sx={{
        maxWidth: '100%',
        background: 'none',
        border: 0,
        padding: 0,
        margin: 0,
        font: 'inherit',
        color: palette.interactive,
        cursor: 'pointer',
        textAlign: 'left',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        '&:hover': { textDecoration: 'underline' },
      }}
    >
      {name}
    </Box>
  );
};

// New row component for adding budget lines
const NewBudgetRow = memo(({
  formValidation,
  handleSaveNewRow,
  savingNewRow,
  singleAmount = false
}) => {
  const { data, errors, updateField, touchField, hasFieldError } = formValidation;
  // 'UI Tips' OFF (#1282) silences the indigo required-empty outline here, same as
  // on the amount cells. A real, non-empty validation error still shows rose.
  const { uiTips } = useUiPrefs();
  // Empty payee = the indigo required prompt (#1279), never the rose error - matches
  // the ledger payee + the amount cells. Red is reserved for a real, non-empty error.
  const payeeEmpty = !data.payee || data.payee.trim() === '';
  const shortDate = data.date && data.date.length >= 10
    ? `${data.date.slice(5, 7)}/${data.date.slice(8, 10)}/${data.date.slice(2, 4)}`
    : 'Pick';

  return (
    <TableRow sx={(theme) => ({
      // v4 (#1377, convention 4): the inline quick-add row reads as the
      // board's recessed strip, hairline-bounded.
      backgroundColor: theme.palette.surface.recessed,
      height: '48px',
      '& > *': { borderBottom: `1px solid ${theme.palette.divider}` },
      '& .MuiTableCell-root': {
        padding: '6px 8px',
        fontSize: '0.875rem'
      }
    })}>
      {/* Date Cell */}
      <TableCell>
        <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '100%' }}>
          <TextField
            type="date"
            inputProps={{ min: TRANSACTION_DATE_MIN, max: transactionDateMax() }}
            value={data.date || ''}
            onChange={(e) => updateField('date', e.target.value)}
            onBlur={() => touchField('date')}
            size="small"
            fullWidth
            error={hasFieldError('date')}
            helperText={errors.date}
          />
        </Box>
        <Box sx={{
          display: { xs: 'inline-flex', sm: 'none' },
          position: 'relative',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid',
          borderColor: hasFieldError('date') ? 'error.main' : 'divider',
          borderRadius: 1,
          height: 28,
          minWidth: 48,
          px: 0.5,
          fontSize: '0.65rem',
          cursor: 'pointer'
        }}>
          <span>{shortDate}</span>
          <Box component="input"
            type="date"
            min={TRANSACTION_DATE_MIN}
            max={transactionDateMax()}
            value={data.date || ''}
            onChange={(e) => updateField('date', e.target.value)}
            onBlur={() => touchField('date')}
            sx={{
              position: 'absolute',
              inset: 0,
              opacity: 0,
              width: '100%',
              height: '100%',
              cursor: 'pointer',
              border: 0,
              padding: 0,
              background: 'transparent',
              color: 'transparent'
            }}
          />
        </Box>
      </TableCell>
      
      {/* Payee Cell */}
      <TableCell>
        <TextField
          size="small"
          fullWidth
          placeholder="Type payee name..."
          value={data.payee || ''}
          onChange={(e) => updateField('payee', e.target.value)}
          onBlur={() => touchField('payee')}
          required
          title={payeeEmpty ? 'Payee is required' : ''}
          error={!payeeEmpty && hasFieldError('payee')}
          helperText={!payeeEmpty ? errors.payee : ''}
          sx={(theme) => ({
            '& .MuiInputBase-input': { fontSize: { xs: '0.65rem', sm: '0.875rem' } },
            '& .MuiInputBase-input::placeholder': { fontSize: { xs: '0.65rem', sm: '0.875rem' } },
            // Empty = indigo required prompt in every state incl. blur (#1279),
            // matching the ledger payee + amount cells. A real, non-empty
            // validation error still gets the MUI error (rose) treatment above.
            // Gated by 'UI Tips' (#1282): off = no indigo prompt.
            ...(payeeEmpty && uiTips ? requiredOutlineSx(theme) : {})
          })}
        />
      </TableCell>
      
      {/* Memo Cell - empty for new rows: budget lines carry no memo here (#2119) */}
      <TableCell />

      {/* Amount Cell (unified debit/credit + save) */}
      <TableCell align="center" data-tour-id="budget-new-amount">
        <Box display="flex" alignItems="center" gap={0.5}>
          <AmountInputCell
            singleAmount={singleAmount}
            debitValue={data.debit}
            creditValue={data.credit}
            onDebitChange={(val) => {
              updateField('debit', val);
              if (val) updateField('credit', '');
            }}
            onCreditChange={(val) => {
              updateField('credit', val);
              if (val) updateField('debit', '');
            }}
          />
          <IconButton size="small" data-tour-id="budget-new-save" onClick={handleSaveNewRow} disabled={savingNewRow} color="primary">
            {savingNewRow ? <DollarSpinner size="small" /> : <SaveIcon />}
          </IconButton>
        </Box>
        {hasFieldError('amount') && (
          <FormHelperText error sx={{ fontSize: '0.7rem', mt: 0.5 }}>
            {errors.amount}
          </FormHelperText>
        )}
      </TableCell>

      {/* Ledger Cell - Empty for new rows */}
      <TableCell align="center">
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          -
        </Typography>
      </TableCell>

      {/* Balance Cell - empty for new rows */}
      <TableCell />

      {/* Actions Cell - empty for new rows */}
      <TableCell />
    </TableRow>
  );
});

// Memoized desktop row (#1790). Non-editing rows receive ONLY stable props: the
// line, its running balance, per-row booleans, and useCallback handlers. The
// edit-only props (editData through handleEditCancel) reach the editing row
// alone; several change identity per keystroke and would otherwise defeat memo
// on every row.
const BudgetTableRow = memo(forwardRef(({
  line,
  runningBalance,
  isEditing = false,
  isHighlighted = false,
  canEdit = true,
  menuDisabled = false,
  formatDate,
  onEdit,
  onDelete,
  editData,
  setEditData,
  saving = false,
  singleAmount = false,
  handleEditSave,
  handleEditCancel
}, ref) => {
  const { palette } = useTheme();
  const isLedgerLine = line.sourceType === 'ledger_line';
  return (
    <TableRow
      ref={ref}
      data-line-id={line.lineId}
      sx={(theme) => ({
        height: '48px',
        // Highlight animation for repositioned lines
        ...(isHighlighted && {
          animation: 'highlightFlash 0.35s ease-in-out 2'
        }),
        '@keyframes highlightFlash': {
          '0%, 100%': { backgroundColor: 'inherit' },
          '50%': { backgroundColor: alpha(theme.palette.success.main, 0.3) }
        },
        '& .MuiTableCell-root': {
          padding: '6px 8px',
          fontSize: '0.75rem'
        }
      })}
    >
      {/* Date Cell */}
      <TableCell align="left" sx={{ color: palette.text.secondary }}>
        {isEditing ? (
          <TextField
            type="date"
            inputProps={{ min: DATE_STAMP_MIN, max: DATE_STAMP_MAX }}
            value={editData.date || ''}
            onChange={(e) => setEditData(prev => ({ ...prev, date: e.target.value }))}
            size="small"
            fullWidth
            sx={{
              '& .MuiInputBase-root': { height: '32px' },
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }
            }}
          />
        ) : (
          <>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {formatDate(line.dateStamp, 'date-only')}
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              {formatDate(line.dateStamp, 'date-short')}
            </Box>
          </>
        )}
      </TableCell>

      {/* Payee Cell */}
      <TableCell>
        {isEditing ? (
          <TextField
            size="small"
            fullWidth
            placeholder="Type payee name..."
            value={editData.payee || ''}
            onChange={(e) => setEditData(prev => ({ ...prev, payee: e.target.value }))}
            sx={{
              '& .MuiInputBase-root': { height: '32px' },
              '& .MuiInputBase-input': { fontSize: { xs: '0.65rem', sm: '0.875rem' } },
              '& .MuiInputBase-input::placeholder': { fontSize: { xs: '0.65rem', sm: '0.875rem' } }
            }}
          />
        ) : (
          line.payeeName
        )}
      </TableCell>

      {/* Memo Cell (#2119) - a ledger line's memo, display only; budget-line
          memos (payday funding, true-up) stay hidden. The flex wrapper lets
          noWrap truncate inside the auto-layout table, as on the Ledger row. */}
      <TableCell align="left">
        {isLedgerLine && line.memo ? (
          <Box sx={{ display: 'flex', minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 'inherit', color: palette.text.secondary, minWidth: 0 }}>
              {line.memo}
            </Typography>
          </Box>
        ) : null}
      </TableCell>

      {/* Amount Cell (unified debit/credit) */}
      <TableCell align="right">
        {isEditing ? (
          <Box display="flex" alignItems="center" gap={0.5}>
            <AmountInputCell
              singleAmount={singleAmount}
              debitValue={editData.debit}
              creditValue={editData.credit}
              onDebitChange={(val) => setEditData(prev => ({ ...prev, debit: val, credit: val ? '' : prev.credit }))}
              onCreditChange={(val) => setEditData(prev => ({ ...prev, credit: val, debit: val ? '' : prev.debit }))}
            />
            <IconButton
              size="small"
              onClick={() => handleEditSave(line)}
              disabled={saving}
              color="primary"
              sx={{ p: 0.25, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
            >
              {saving ? <DollarSpinner size="small" /> : <SaveIcon />}
            </IconButton>
            <IconButton
              size="small"
              onClick={handleEditCancel}
              disabled={saving}
              sx={{ p: 0.25, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
            >
              <CancelIcon />
            </IconButton>
          </Box>
        ) : (
          <AmountDisplay
            amountCents={line.amountCents}
            variant="row"
          />
        )}
      </TableCell>

      {/* Ledger Cell */}
      <TableCell align="left">
        {isLedgerLine ? (
          <LedgerLink ledgerId={line.ledgerId} name={line.ledgerName} />
        ) : '-'}
      </TableCell>

      {/* Balance Cell - running subtotal, colored per DESIGN_SYSTEM section 11 */}
      <TableCell align="right">
        <Typography component="span" fontWeight="medium">
          <MaskedCurrency
            value={runningBalance}
            style={{
              color: resolveAmountColor(
                palette,
                'subtotal',
                runningBalance < 0,
                runningBalance === 0
              ),
            }}
          />
        </Typography>
      </TableCell>

      {/* Actions Cell - editor/owner only */}
      <TableCell align="center">
        {isLedgerLine ? null : !isEditing && canEdit && (
          <ActionMenu
            onEdit={() => onEdit(line)}
            onDelete={() => onDelete(line)}
            disabled={menuDisabled}
          />
        )}
      </TableCell>
    </TableRow>
  );
}));
BudgetTableRow.displayName = 'BudgetTableRow';

// v4 mobile card for one NON-editing line (#1790). Same memo contract as the
// desktop row above; the editing card stays inline in the page.
const BudgetLineCard = memo(forwardRef(({
  line,
  canEdit = true,
  menuDisabled = false,
  onEdit,
  onDelete
}, ref) => {
  const { palette } = useTheme();
  const isLedgerLine = line.sourceType === 'ledger_line';
  return (
    <Box
      ref={ref}
      data-line-id={line.lineId}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '11px',
        padding: '9px 0',
        borderBottom: `1px solid ${palette.divider}`,
        '&:last-child': { borderBottom: 0 },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '14px', color: palette.text.primary }}>
          {line.payeeName && line.payeeName.trim() ? line.payeeName : 'Unknown Payee'}
        </Typography>
        <Typography noWrap component="div" sx={{ fontSize: '12px', color: palette.text.dim, mt: '1px' }}>
          {isLedgerLine ? <LedgerLink ledgerId={line.ledgerId} name={line.ledgerName} /> : '-'}
        </Typography>
        {/* #2119: a ledger line's memo, only when it has one */}
        {isLedgerLine && line.memo ? (
          <Typography noWrap sx={{ fontSize: '12px', color: palette.text.dim, mt: '2px' }}>
            {line.memo}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <AmountDisplay
          amountCents={line.amountCents}
          variant="row"
        />
      </Box>
      {canEdit && (
        isLedgerLine ? (
          // #1475: ledger-sourced rows are read-only (no menu).
          // Reserve the menu's width with a hidden placeholder so
          // the amount column lines up with the editable rows.
          <IconButton
            size="small"
            disabled
            aria-hidden
            tabIndex={-1}
            sx={{ visibility: 'hidden' }}
          >
            <MoreVertIcon />
          </IconButton>
        ) : (
          <ActionMenu
            onEdit={() => onEdit(line)}
            onDelete={() => onDelete(line)}
            disabled={menuDisabled}
          />
        )
      )}
    </Box>
  );
}));
BudgetLineCard.displayName = 'BudgetLineCard';

const BudgetDetail = ({ isAuthReady = false }) => {
  const { id } = useParams();
  const { activeGroupId } = useGroup();
  const canEdit = useCanEdit();
  const isOwner = useIsOwner();
  const [unarchiving, setUnarchiving] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const { palette, ds } = theme;
  // v4 (#1377, convention 4): xs gets the quick-add Sheet from the ActionBar;
  // sm+ keeps the inline row form.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // #1465: also drop to cards when the desktop table would ACTUALLY overflow
  // (narrow window / zoom), detected off the live table with a stable probe for the
  // non-oscillating restore. useCardView replaces isMobile wherever the choice is
  // "show cards"; tableContainerRef is the desktop table's overflow-x container.
  const tableContainerRef = useRef(null);
  const [tableOverflows, overflowProbeRef] = useOverflowCardSwitch(tableContainerRef);
  const useCardView = isMobile || tableOverflows;
  const [addSheetOpen, setAddSheetOpen] = useState(false);
  const { formatDate } = useTimezoneAwareDateFormatting();
  const { showSuccess, showError, showWarning } = useToast();
  const { payees, addPayee } = usePayees();
  const [budget, setBudget] = useState(null);
  // #2175: the hero's horizon chip opens the switch dialog for this budget.
  const [horizonDialogOpen, setHorizonDialogOpen] = useState(false);
  const [balance, setBalance] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noTransactionsExist, setNoTransactionsExist] = useState(false);
  
  // Edit/Delete state variables
  const [editingLine, setEditingLine] = useState(null);
  const [editData, setEditData] = useState({
    date: '',
    payee: '',
    debit: '',
    credit: ''
  });
  const [saving, setSaving] = useState(false);
  const [highlightedLineId, setHighlightedLineId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, line: null });

  // Form validation for new row
  const newRowFormValidation = useTransactionFormValidation({
    date: getLocalDateString(),
    payee: '',
    debit: '',
    credit: ''
  });

  // Load budget lines for infinite scroll. Cursor-based (#1292): given the
  // server's opaque nextCursor (undefined for the first page), returns one page
  // of lines plus the cursor for the following page.
  const loadBudgetLinesData = useCallback(async (cursor) => {
    if (!activeGroupId) return { lines: [], nextCursor: null };
    const qs = new URLSearchParams({ budgetId: String(id), order: 'desc' });
    if (cursor) qs.set('cursor', cursor);
    const response = await api.get(activeGroupId, `/lines?${qs.toString()}`);
    return { lines: response.lines || [], nextCursor: response.nextCursor || null };
  }, [id, activeGroupId]);

  // Infinite scroll hook
  const {
    data: budgetLines,
    loading,
    loadingMore,
    hasMore,
    autoLoadPaused,
    error: scrollError,
    loadInitialData,
    lastElementRef,
    loadMore,
    updateData,
  } = useInfiniteScroll(loadBudgetLinesData);

  // #1933: the just-saved line renders first until the user acts (next add,
  // reload) or it leaves the rendered set. Presentation only: budgetLines
  // keeps its sort order; the line is drawn once, at the top.
  const { pinnedIndex, pin: pinLine, release: releasePin } = usePinnedLine(budgetLines);

  // Compute running balances from current balance and budget lines
  const runningBalances = useMemo(() => {
    return computeRunningBalances((balance?.balanceCents ?? 0) / 100, budgetLines);
  }, [balance?.balanceCents, budgetLines]);

  // Render order (#1933): the pinned line first, then the rest in sort order.
  // Entries are indexes into budgetLines so running balances line up. The
  // scroll sentinel goes on the last entry, so a pinned line that happens to
  // be the oldest loaded row never carries it to the top of the page.
  const renderOrder = useMemo(() => {
    const order = budgetLines.map((_, idx) => idx);
    if (pinnedIndex > 0) {
      order.splice(pinnedIndex, 1);
      order.unshift(pinnedIndex);
    }
    return order;
  }, [budgetLines, pinnedIndex]);
  const sentinelIndex = renderOrder.length > 0 ? renderOrder[renderOrder.length - 1] : -1;

  // v4 mobile (mirrors LedgerDetail #1377): date-grouped rows. Lines arrive
  // sorted desc, so each group's first row carries that date's end-of-day
  // running balance. Guarded off on desktop where the table renders instead.
  // The pinned line (#1933) is its own group at the top; it stays in its
  // date's header balance (the end-of-day figure does not change) and only
  // its card is left out of that group.
  const mobileGroups = useMemo(() => {
    if (!useCardView) return [];
    const groups = [];
    let current = null;
    budgetLines.forEach((line, idx) => {
      if (!current || current.date !== line.dateStamp) {
        current = { key: line.dateStamp, date: line.dateStamp, balance: runningBalances[idx], rows: [] };
        groups.push(current);
      }
      if (idx !== pinnedIndex) {
        current.rows.push({ line, index: idx });
      }
    });
    const dated = groups.filter((g) => g.rows.length > 0);
    if (pinnedIndex === -1) return dated;
    const pinned = budgetLines[pinnedIndex];
    return [
      {
        key: 'pinned',
        pinned: true,
        date: pinned.dateStamp,
        balance: runningBalances[pinnedIndex],
        rows: [{ line: pinned, index: pinnedIndex }],
      },
      ...dated,
    ];
  }, [useCardView, budgetLines, runningBalances, pinnedIndex]);

  const [savingNewRow, setSavingNewRow] = useState(false);

  const loadBudgetData = useCallback(async () => {
    try {
      setInitialLoading(true);
      setError(null);
      setNoTransactionsExist(false);
      // A reload settles the pinned line into date order (#1933).
      releasePin();

      if (!activeGroupId) return;

      const [budgetResponse, balanceResponse] = await Promise.all([
        api.get(activeGroupId, `/budgets/${encodeURIComponent(id)}`),
        api.get(activeGroupId, `/budgets/${encodeURIComponent(id)}/balance?window=trueup`),
      ]);

      setBudget(budgetResponse.budget);
      setBalance(balanceResponse);

      // Cursor pagination loads the newest page first; an empty first page means
      // the budget has no lines (replaces the old /lines/recent-month probe).
      const firstPage = await loadInitialData();
      setNoTransactionsExist(firstPage.length === 0);

    } catch (err) {
      console.error('Failed to load budget data:', err);
      setError('Failed to load budget data. Please try again.');
    } finally {
      setInitialLoading(false);
    }
  }, [id, loadInitialData, releasePin]);

  const handleUnarchive = useCallback(async () => {
    if (!budget) return;
    try {
      setUnarchiving(true);
      setError(null);
      const response = await api.put(activeGroupId, `/budgets/${encodeURIComponent(budget.budgetId)}`, {
        archived: false,
        version: budget.version,
      });
      setBudget(response.budget);
      showSuccess(`Restored "${budget.name}"`);
    } catch (err) {
      console.error('Failed to unarchive budget:', err);
      showError(err.message || 'Failed to unarchive budget.');
    } finally {
      setUnarchiving(false);
    }
  }, [budget, activeGroupId, showSuccess, showError]);

  // Effect to fill viewport with data - keep loading months until we have enough rows
  useEffect(() => {
    const fillViewport = async () => {
      // Only run after initial loading is complete
      if (initialLoading || loading || loadingMore || !hasMore || autoLoadPaused) return;
      
      // Check if we need more data to fill the viewport
      const container = tableContainerRef.current;
      if (!container) return;
      
      const viewportHeight = window.innerHeight;
      const containerRect = container.getBoundingClientRect();
      const contentHeight = container.scrollHeight;
      const availableSpace = viewportHeight - containerRect.top;
      
      // If content doesn't fill the available space, load more
      // Add buffer of 100px to ensure we have some extra rows for smooth scrolling
      if (contentHeight < availableSpace + 100 && budgetLines.length > 0) {
        await loadMore();
      }
    };

    fillViewport();
  }, [initialLoading, loading, loadingMore, hasMore, autoLoadPaused, budgetLines.length, loadMore]);

  // Cache refresh function for balance displays

  useEffect(() => {
    // Only load data after authentication is confirmed
    if (isAuthReady) {
      loadBudgetData();
    }
  }, [id, loadBudgetData, isAuthReady]);

  // Resolve a free-text payee name to a payeeId (find or create).
  const resolvePayeeId = useCallback(async (nameRaw) => {
    const name = (nameRaw || '').trim();
    if (!name) return null;
    const existing = payees.find(p => p.name.toLowerCase() === name.toLowerCase());
    if (existing) return existing.payeeId;
    const created = await addPayee(name);
    return created?.payeeId || null;
  }, [payees, addPayee]);



  const handleSaveNewRow = async () => {
    try {
      setSavingNewRow(true);

      // Validate form
      if (!newRowFormValidation.validateAll()) {
        showWarning('Please fix the validation errors before saving');
        setSavingNewRow(false);
        return false;
      }

      const dateStamp = newRowFormValidation.data.date;
      const debit = parseAmountInput(newRowFormValidation.data.debit);
      const credit = parseAmountInput(newRowFormValidation.data.credit);
      const amountCents = Math.round((credit - debit) * 100);
      const payeeId = await resolvePayeeId(newRowFormValidation.data.payee);

      const body = {
        sourceType: 'budget_line',
        budgetId: String(id),
        dateStamp,
        amountCents,
        memo: '',
      };
      if (payeeId) body.payeeId = payeeId;

      const response = await api.post(activeGroupId, '/lines', body);
      const newLine = response.line;
      notifyTangibleRefresh();

      newRowFormValidation.reset({
        date: getLocalDateString(),
        payee: '',
        debit: '',
        credit: ''
      });

      setNoTransactionsExist(false);

      if (newLine) {
        // updateData re-sorts, so a past-dated line lands in date order (#1845).
        updateData(prevLines => [newLine, ...prevLines]);
        // Keep it in view for the spot check until the next entry (#1933).
        pinLine(newLine.lineId);
      }

      setBalance(prevBalance => ({
        ...prevBalance,
        balanceCents: (prevBalance?.balanceCents ?? 0) + amountCents,
        // #1384: keep the optimistic aggregates consistent so the bar tracks
        // the new line; the fresh fetch below replaces them anyway.
        ...(prevBalance?.fundedCents != null ? {
          fundedCents: prevBalance.fundedCents + (amountCents > 0 ? amountCents : 0),
          spentCents: (prevBalance.spentCents ?? 0) + (amountCents < 0 ? -amountCents : 0),
        } : {}),
      }));

      setTimeout(async () => {
        try {
          const freshBalanceResponse = await api.get(
            activeGroupId,
            `/budgets/${encodeURIComponent(id)}/balance?window=trueup`
          );
          setBalance(freshBalanceResponse);
        } catch (err) {
          console.error('Failed to refresh balance after line creation:', err);
        }
      }, 500);

      showSuccess('Transaction saved successfully');
      // Success signal so the mobile quick-add Sheet can close (#1377).
      return true;

    } catch (err) {
      console.error('Failed to save new budget line:', err);

      // Handle validation errors from server
      if (err.response?.data?.error?.type === 'VALIDATION_ERROR') {
        const serverError = err.response.data.error;
        showError(`Validation Error: ${serverError.message}`);
      } else {
        const errorMessage = err.message || 'Failed to save new budget line. Please try again.';
        showError(errorMessage);
      }

      // Revert optimistic updates on error by reloading data
      await loadBudgetData();
      return false;
    } finally {
      setSavingNewRow(false);
    }
  };

  /**
   * Enter edit mode for a budget line
   * @param {Object} line - The budget line object to edit
   */
  const handleEditClick = useCallback((line) => {
    const amt = typeof line.amountCents === 'number' ? line.amountCents : 0;
    setEditingLine(line.lineId);
    setEditData({
      date: line.dateStamp,
      payee: line.payeeName || '',
      debit: amt < 0 ? (-amt / 100).toFixed(2) : '',
      credit: amt > 0 ? (amt / 100).toFixed(2) : '',
    });
  }, []);

  /**
   * Save edited budget line to server
   * @param {Object} line - The original budget line object
   */
  const handleEditSave = useCallback(async (line) => {
    try {
      setSaving(true);

      // Validate required fields - payee is required
      if (!editData.payee || editData.payee.trim() === '') {
        showError('Payee is required');
        setSaving(false);
        return;
      }

      // Validate that at least one of debit/credit is provided
      const hasDebit = editData.debit !== '' && editData.debit !== null && editData.debit !== undefined;
      const hasCredit = editData.credit !== '' && editData.credit !== null && editData.credit !== undefined;
      if (!hasDebit && !hasCredit) {
        showError('Please enter either a debit or credit amount');
        setSaving(false);
        return;
      }

      // Shape plus a real-calendar check (#1842): the old regex took any
      // four-digit year, month 13 and day 32.
      if (!isValidDateStamp(editData.date)) {
        showError(INVALID_DATE_MESSAGE);
        setSaving(false);
        return;
      }

      const debit = hasDebit ? parseAmountInput(editData.debit) : 0;
      const credit = hasCredit ? parseAmountInput(editData.credit) : 0;
      const amountCents = Math.round((credit - debit) * 100);
      const payeeId = await resolvePayeeId(editData.payee);

      const dateChanged = line.dateStamp !== editData.date;

      // Optimistic UI update with v2 fields
      updateData(prevLines => {
        const updatedLines = prevLines.map(l =>
          l.lineId === line.lineId
            ? {
                ...l,
                dateStamp: editData.date,
                yearMonth: editData.date.slice(0, 7),
                amountCents,
                payeeName: editData.payee.trim(),
                ...(payeeId ? { payeeId } : {}),
              }
            : l
        );
        if (dateChanged) {
          return updatedLines.sort((a, b) => {
            if (a.dateStamp !== b.dateStamp) {
              return b.dateStamp.localeCompare(a.dateStamp);
            }
            return compareSameDateDesc(a, b);
          });
        }
        return updatedLines;
      });

      setEditingLine(null);
      setEditData({ date: '', payee: '', debit: '', credit: '' });

      if (dateChanged) {
        setHighlightedLineId(line.lineId);
        setTimeout(() => {
          const lineElement = document.querySelector(`[data-line-id="${line.lineId}"]`);
          if (lineElement) {
            lineElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          setTimeout(() => setHighlightedLineId(null), 700);
        }, 100);
      }

      const yearMonth = line.yearMonth || line.dateStamp.slice(0, 7);
      const body = {
        version: line.version,
        dateStamp: editData.date,
        amountCents,
      };
      if (payeeId) body.payeeId = payeeId;

      const response = await api.put(
        activeGroupId,
        `/lines/${encodeURIComponent(yearMonth)}/${encodeURIComponent(line.lineId)}`,
        body
      );

      // Merge bumped version (and any other server-side changes) back into
      // local state so a rapid follow-up edit reads fresh version (#639).
      const updated = response?.line;
      if (updated) {
        updateData(prevLines =>
          prevLines.map(l => l.lineId === line.lineId ? { ...l, ...updated } : l)
        );
      }
      notifyTangibleRefresh();

      setTimeout(async () => {
        try {
          const freshBalanceResponse = await api.get(
            activeGroupId,
            `/budgets/${encodeURIComponent(id)}/balance?window=trueup`
          );
          setBalance(freshBalanceResponse);
        } catch (err) {
          console.error('Failed to refresh balance after line update:', err);
        }
      }, 1000);

      showSuccess('Transaction updated successfully');

    } catch (err) {
      console.error('Failed to save budget line:', err);
      showError('Failed to save changes. Please try again.');

      // Revert optimistic update on error by reloading data
      await loadBudgetData();
      
      // Clear highlight on error
      setHighlightedLineId(null);
      
      // Exit edit mode on error
      setEditingLine(null);
      setEditData({ date: '', payee: '', debit: '', credit: '' });
    } finally {
      setSaving(false);
    }
  }, [activeGroupId, editData, updateData, id, showSuccess, showError, loadBudgetData]);

  /**
   * Cancel edit mode without saving
   */
  const handleEditCancel = useCallback(() => {
    setEditingLine(null);
    setEditData({ date: '', payee: '', debit: '', credit: '' });
  }, []);

  /**
   * Open the delete-confirmation dialog for a budget line
   * @param {Object} line - The budget line object to delete
   */
  const handleDeleteClick = useCallback((line) => {
    setDeleteDialog({ open: true, line });
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialog({ open: false, line: null });
  }, []);

  // Props only the editing desktop row needs (#1790). Built per render and
  // spread onto that one row; every other row keeps a stable prop set so
  // React.memo holds while the user types.
  const editRowProps = {
    editData,
    setEditData,
    saving,
    singleAmount: budget?.type === 2,
    handleEditSave,
    handleEditCancel,
  };

  /**
   * Delete the budget line pending in the confirmation dialog
   */
  const handleDeleteConfirm = useCallback(async () => {
    const line = deleteDialog.line;
    setDeleteDialog({ open: false, line: null });
    if (!line) {
      return;
    }

    // Store original line for potential rollback
    const originalLine = { ...line };
    
    try {
      updateData(prevLines =>
        prevLines.filter(l => l.lineId !== line.lineId)
      );

      const yearMonth = line.yearMonth || line.dateStamp.slice(0, 7);
      await api.delete(
        activeGroupId,
        `/lines/${encodeURIComponent(yearMonth)}/${encodeURIComponent(line.lineId)}?version=${line.version}`
      );
      notifyTangibleRefresh();

      setTimeout(async () => {
        try {
          const freshBalanceResponse = await api.get(
            activeGroupId,
            `/budgets/${encodeURIComponent(id)}/balance?window=trueup`
          );
          setBalance(freshBalanceResponse);
        } catch (err) {
          console.error('Failed to refresh balance after line deletion:', err);
        }
      }, 500);

      showSuccess('Transaction deleted successfully');

    } catch (error) {
      console.error('Failed to delete budget line:', error);
      showError('Failed to delete budget line. Please try again.');
      
      // Restore deleted line to list on failure (sort newest first).
      updateData(prevLines => {
        const newLines = [...prevLines, originalLine];
        return newLines.sort((a, b) => {
          if (a.dateStamp !== b.dateStamp) {
            return b.dateStamp.localeCompare(a.dateStamp);
          }
          return compareSameDateDesc(a, b);
        });
      });
    }
  }, [activeGroupId, id, updateData, showSuccess, showError, deleteDialog]);

  if (!isAuthReady || initialLoading) {
    return <CenteredLoader message="Loading budget..." />;
  }

  if (error || scrollError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error || scrollError}
      </Alert>
    );
  }

  return (
    <Box sx={{ pb: 8 }}>
      {/* #1465 overflow probe: a zero-height, full-width sentinel whose measured
          width drives the table->cards switch. It stays mounted in both layouts,
          so the measurement is never stretched by the wide table itself. */}
      <Box ref={overflowProbeRef} aria-hidden sx={{ height: 0 }} />
      {/* v4 sticky detail hero (#1377 board): back link, then one line -
          22px category glyph + 22px name + 22px sign-colored balance + 12px
          type label - with the progress bar + spent line beneath
          (convention 6). No kebab; the header ends with the top-bar avatar
          (convention 1). */}
      {(() => {
        const isPendingIncome = budget?.type === 3;
        const hasBalance = balance?.balanceCents != null;
        const displayCents = hasBalance
          ? (isPendingIncome ? -balance.balanceCents : balance.balanceCents)
          : 0;
        const typeLabel = { 0: 'General', 2: 'Planned Expenses', 3: 'Pending Income' }[budget?.type] || '';
        // #1127: per-budget AI-picked icon (falls back to the placeholder).
        const BudgetIcon = resolveBudgetIcon(budget?.icon);

        // Progress bar (convention 6). #1420: the bar window is the FUNDING
        // CYCLE - everything since the last true-up entry (a true-up resets
        // the budget to zero, so "Spent X of Y" restarts there). The balance
        // endpoint serves the pair via ?window=trueup; the lifetime #1384
        // pair stays in the response for other consumers but never feeds the
        // bar. Fallback (fields absent, deploy order): walk the loaded lines
        // newest-first to the last true-up marker (system payee 'True-Up',
        // positive amount; lineSubtype is not GSI-projected, #1347); valid
        // once a boundary is among the loaded lines or every line is in.
        // Lines sharing the boundary dateStamp stay in the window (payday
        // funding lands the same day as its true-up). Pending Income has no
        // spend semantics - no bar.
        const serverTotals = (!isPendingIncome
          && balance?.windowFundedCents != null && balance?.windowSpentCents != null)
          ? { fundedCents: balance.windowFundedCents, spentCents: balance.windowSpentCents }
          : null;
        const deriveTrueUpWindow = () => {
          const acc = { fundedCents: 0, spentCents: 0 };
          let boundaryDate = null;
          for (const l of budgetLines) {
            if (boundaryDate !== null && (l.dateStamp || '') < boundaryDate) {
              return acc;
            }
            // A release (ru: lineId, #2103) resets to zero the same way.
            if ((l.payeeName === 'True-Up' && (l.amountCents || 0) > 0) || (l.lineId || '').startsWith('ru:')) {
              boundaryDate = l.dateStamp || '';
              continue;
            }
            const amt = l.amountCents || 0;
            if (amt >= 0) acc.fundedCents += amt; else acc.spentCents += -amt;
          }
          // No boundary found: only trustworthy when the whole history is in.
          return (boundaryDate !== null || !hasMore) ? acc : null;
        };
        const lineTotals = serverTotals || (!isPendingIncome ? deriveTrueUpWindow() : null);
        const seg = lineTotals ? budgetBarSegments(lineTotals.fundedCents, lineTotals.spentCents) : null;
        const overCents = seg?.over ? lineTotals.spentCents - lineTotals.fundedCents : 0;

        const barNode = seg && (lineTotals.fundedCents > 0 || lineTotals.spentCents > 0) ? (
          <Box sx={{ marginTop: { xs: '11px', sm: '10px' } }}>
            <Box
              data-testid="budget-progress-bar"
              sx={{
                height: 3,
                borderRadius: '2px',
                overflow: 'hidden',
                display: 'flex',
                // OVER: no track - the amber overspend share renders at the
                // left origin, green (budgeted share) fills the rest.
                backgroundColor: seg.over ? 'transparent' : palette.surface.hover,
              }}
            >
              {seg.over ? (
                <>
                  <Box sx={{ flex: `0 0 ${seg.amberPct}%`, backgroundColor: palette.cat.income }} />
                  <Box sx={{ flex: 1, backgroundColor: palette.cat.budgets }} />
                </>
              ) : (
                <Box sx={{ width: `${seg.fillPct}%`, backgroundColor: palette.cat.budgets }} />
              )}
            </Box>
            <Box
              sx={{
                marginTop: '6px',
                fontSize: '12px',
                color: palette.text.dim,
                fontVariantNumeric: 'tabular-nums',
                display: 'flex',
                justifyContent: { xs: 'space-between', sm: 'flex-start' },
                gap: '4px',
                flexWrap: 'wrap',
              }}
            >
              <span>
                {'Spent '}<MaskedCurrency value={lineTotals.spentCents / 100} />
                {' of '}<MaskedCurrency value={lineTotals.fundedCents / 100} />
              </span>
              {seg.over && (
                <Box component="span" sx={{ color: palette.error.main }}>
                  {'· '}<MaskedCurrency value={overCents / 100} />{' over'}
                </Box>
              )}
            </Box>
          </Box>
        ) : null;

        const label = (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: `${ds.space[4]}px`, flexWrap: 'wrap', lineHeight: 1.15 }}>
            <Box
              aria-hidden
              sx={{
                color: iconBoxColors(theme, 'budgets').accent,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, '& svg': { width: 22, height: 22 },
              }}
            >
              <BudgetIcon />
            </Box>
            <Box component="span" sx={{ fontSize: { xs: '20px', sm: '22px' }, fontWeight: ds.font.weightMedium, color: palette.text.primary }}>
              {budget?.name}
            </Box>
            <Box
              component="span"
              sx={{
                // Mobile board hero: prominent 26px balance on its own line.
                fontSize: { xs: '26px', sm: '22px' },
                width: { xs: '100%', sm: 'auto' },
                letterSpacing: ds.font.letterHero,
                fontWeight: ds.font.weightMedium,
                fontVariantNumeric: 'tabular-nums',
                color: hasBalance
                  ? resolveAmountColor(palette, 'total', displayCents < 0, displayCents === 0)
                  : palette.text.dim,
              }}
            >
              {hasBalance ? <MaskedCurrency value={displayCents / 100} /> : '--'}
            </Box>
            <Box component="span" sx={{ fontSize: '12px', color: palette.text.dim }}>
              {typeLabel}
            </Box>
            {budget?.type === 0 && !budget.archived && (
              <HorizonChip wide horizon={horizonOf(budget)} onPress={canEdit ? () => setHorizonDialogOpen(true) : undefined} />
            )}
            {budget?.archived && (
              <>
                <Chip label="Archived" color="default" size="small" sx={{ opacity: 0.85 }} />
                {isOwner && (
                  <Tooltip title="Restore this budget to the active list">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Unarchive budget"
                        onClick={handleUnarchive}
                        disabled={unarchiving}
                      >
                        <UnarchiveIcon size={20} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        );
        return (
          <PageHero
            back={{ label: 'Budgets', onClick: () => navigate('/budgets') }}
            label={label}
            context={
              <>
                {isPendingIncome && (
                  <Box component="span" sx={{ fontStyle: 'italic' }}>
                    Subtotal flipped so debts read like assets.
                  </Box>
                )}
                {barNode}
              </>
            }
            sx={{
              position: 'sticky',
              // Track the bar's responsive minHeight exactly (48 xs / 52 sm+)
              // so no see-through sliver can open between bar and hero.
              top: '44px',
              zIndex: theme.zIndex.appBar - 1,
              backgroundColor: palette.surface.raised,
              marginBottom: `${ds.space[6]}px`,
            }}
          />
        );
      })()}

      {/* v4 mobile (mirrors LedgerDetail #1377): date-grouped bare cards; the
          quick-add Sheet lives below. sm+ keeps the desktop table + inline row. */}
      {useCardView && (
        <Box ref={tableContainerRef} sx={{ mt: '12px', pb: canEdit ? '96px' : 0 }}>
          {loading && budgetLines.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <DollarSpinner size="large" message="Loading transactions..." />
            </Box>
          )}
          {!loading && budgetLines.length === 0 && noTransactionsExist && (
            <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
              No transactions yet - add your first one below
            </Typography>
          )}
          {mobileGroups.map((g) => (
            <Box key={g.key} data-pinned-line={g.pinned ? g.rows[0].line.lineId : undefined} sx={{ mb: '13px' }}>
              {/* Group header: category dot + date + end-of-day running balance */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '6px', px: '2px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Box sx={{ width: ds.size.dot, height: ds.size.dot, borderRadius: '50%', backgroundColor: iconBoxColors(theme, 'budgets').accent }} />
                  <Box sx={{ fontSize: '12px', fontWeight: ds.font.weightMedium, color: palette.text.secondary }}>
                    {formatDate(g.date, 'date-only')}
                  </Box>
                </Box>
                <Box sx={{ fontSize: '12px', color: palette.text.dim, fontVariantNumeric: 'tabular-nums' }}>
                  <MaskedCurrency value={g.balance} />
                </Box>
              </Box>
              <Box sx={{ backgroundColor: palette.surface.raised, border: `1px solid ${palette.divider}`, borderRadius: `${ds.radius.container}px`, padding: '1px 14px' }}>
                {g.rows.map(({ line, index }) => {
                  const isLastOverall = index === sentinelIndex;
                  if (editingLine !== line.lineId) {
                    // Memoized card (#1790): stable props only, so typing in the
                    // Sheet or the editing card leaves these cards untouched.
                    return (
                      <BudgetLineCard
                        key={line.lineId}
                        ref={isLastOverall ? lastElementRef : null}
                        line={line}
                        canEdit={canEdit}
                        menuDisabled={editingLine !== null}
                        onEdit={handleEditClick}
                        onDelete={handleDeleteClick}
                      />
                    );
                  }
                  // Editing card: one row, re-renders with the page by design.
                  return (
                    <Box
                      key={line.lineId}
                      ref={isLastOverall ? lastElementRef : null}
                      data-line-id={line.lineId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '11px',
                        padding: '9px 0',
                        borderBottom: `1px solid ${palette.divider}`,
                        '&:last-child': { borderBottom: 0 },
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', py: '4px' }}>
                        <TextField
                          type="date"
                          inputProps={{ min: DATE_STAMP_MIN, max: DATE_STAMP_MAX }}
                          size="small"
                          value={editData.date || ''}
                          onChange={(e) => setEditData(prev => ({ ...prev, date: e.target.value }))}
                        />
                        <TextField
                          size="small"
                          placeholder="Type payee name..."
                          value={editData.payee || ''}
                          onChange={(e) => setEditData(prev => ({ ...prev, payee: e.target.value }))}
                        />
                        <AmountInputCell
                          fullRow
                          singleAmount={budget?.type === 2}
                          debitValue={editData.debit}
                          creditValue={editData.credit}
                          onDebitChange={(val) => setEditData(prev => ({ ...prev, debit: val, credit: val ? '' : prev.credit }))}
                          onCreditChange={(val) => setEditData(prev => ({ ...prev, credit: val, debit: val ? '' : prev.debit }))}
                        />
                        <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          <Button size="small" onClick={handleEditCancel} disabled={saving}>Cancel</Button>
                          <Button size="small" variant="contained" color="primary" onClick={() => handleEditSave(line)} disabled={saving}>
                            {saving ? 'Saving...' : 'Save'}
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
          {loadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <DollarSpinner size="medium" />
            </Box>
          )}
          {autoLoadPaused && !loadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<IconChevronDown size={16} />}
                onClick={() => loadMore()}
              >
                Load older transactions
              </Button>
            </Box>
          )}
          {!hasMore && budgetLines.length > 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
              No more transactions to load
            </Typography>
          )}
        </Box>
      )}

      {/* Budget Lines Table - v4 flush-table surface box, no section header */}
      {!useCardView && (
      <Box
        sx={{
          backgroundColor: palette.surface.raised,
          border: `1px solid ${palette.divider}`,
          borderRadius: `${ds.radius.container}px`,
          padding: '0 16px',
          marginTop: { xs: '12px', md: '14px' },
        }}
      >
          {/* Always show the table with NewBudgetRow for data entry */}
          <TableContainer ref={tableContainerRef}>
            <Table 
              sx={{ 
                minWidth: 650,
                '@media (max-width: 900px)': {
                  minWidth: 'unset'
                },
                '@media (min-width: 600px) and (max-width: 900px)': {
                  '& .MuiTableCell-root': {
                    padding: '4px 6px',
                    fontSize: '0.875rem'
                  }
                }
              }}
            >
              {/* v4 board table header (#1377): 11px uppercase dim cells;
                  columns 100/1fr/132/150/190/150/32 on desktop (mirrors Ledger:
                  Memo after Payee (#2119), Amount, then the cross-ref column,
                  then Balance). */}
              <TableHead sx={{ display: { xs: 'none', sm: 'table-header-group' } }}>
                <TableRow
                  sx={{
                    '& .MuiTableCell-root': {
                      fontSize: '11px',
                      fontWeight: ds.font.weightMedium,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: palette.text.dim,
                      padding: '8px 8px 9px',
                    },
                  }}
                >
                  <TableCell align="left" sx={{ width: { sm: '70px', md: '100px' } }}>Date</TableCell>
                  <TableCell align="left" sx={{ minWidth: { sm: '120px' }, width: 'auto' }}>Payee</TableCell>
                  <TableCell align="left" sx={{ width: { sm: '106px', md: '132px' } }}>Memo</TableCell>
                  <TableCell align="right" sx={{ width: { sm: '105px', md: '150px' } }}>Amount</TableCell>
                  <TableCell align="left" sx={{ width: { sm: '120px', md: '190px' } }}>Ledger</TableCell>
                  <TableCell align="right" sx={{ width: { sm: '105px', md: '150px' } }}>Balance</TableCell>
                  <TableCell align="center" sx={{ width: '32px', padding: '0 4px' }}>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* New row for adding budget lines - editor/owner only.
                    v4 convention 4: inline on sm+ only; xs uses the Sheet. */}
                {canEdit && !useCardView && (
                  <NewBudgetRow
                    formValidation={newRowFormValidation}
                    handleSaveNewRow={handleSaveNewRow}
                    savingNewRow={savingNewRow}
                    singleAmount={budget?.type === 2}
                  />
                )}

                {/* Loading indicator - shows during initial load */}
                {loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <DollarSpinner size="large" message="Loading transactions..." />
                    </TableCell>
                  </TableRow>
                )}
                
                {/* Empty state message when no transactions exist */}
                {!loading && budgetLines.length === 0 && noTransactionsExist && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ color: 'text.secondary' }}>
                        No transactions yet - add your first one above
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                
                {/* Existing budget lines */}
                {/* Edit-only props reach the editing row alone (#1790). */}
                {/* The pinned line, if any, renders first (#1933). */}
                {renderOrder.map((index) => {
                  const line = budgetLines[index];
                  const isLast = index === sentinelIndex;
                  const isEditing = editingLine === line.lineId;
                  return (
                    <BudgetTableRow
                      key={line.lineId}
                      ref={isLast ? lastElementRef : null}
                      line={line}
                      runningBalance={runningBalances[index]}
                      isEditing={isEditing}
                      isHighlighted={highlightedLineId === line.lineId}
                      canEdit={canEdit}
                      menuDisabled={editingLine !== null}
                      formatDate={formatDate}
                      onEdit={handleEditClick}
                      onDelete={handleDeleteClick}
                      {...(isEditing ? editRowProps : null)}
                    />
                  );
                })}

                {/* Infinite scroll loading indicator */}
                {loadingMore && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                      <DollarSpinner size="medium" />
                    </TableCell>
                  </TableRow>
                )}

                {/* Auto-load cap reached (#1790): one more page per tap */}
                {autoLoadPaused && !loadingMore && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<IconChevronDown size={16} />}
                        onClick={() => loadMore()}
                      >
                        Load older transactions
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
                
                {/* End of data indicator */}
                {!hasMore && budgetLines.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        No more transactions to load
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
      </Box>
      )}

      {/* v4 mobile quick-add (convention 4): pinned ActionBar + bottom Sheet */}
      {useCardView && canEdit && (
        <>
          <ActionBar>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<IconPlus size={16} />}
              onClick={() => setAddSheetOpen(true)}
              disabled={editingLine !== null}
            >
              Add entry
            </Button>
          </ActionBar>
          <Sheet open={addSheetOpen} onClose={() => setAddSheetOpen(false)} title="Add entry">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <TextField
                label="Date"
                type="date"
                inputProps={{ min: TRANSACTION_DATE_MIN, max: transactionDateMax() }}
                size="small"
                value={newRowFormValidation.data.date || ''}
                onChange={(e) => newRowFormValidation.updateField('date', e.target.value)}
                error={newRowFormValidation.hasFieldError('date')}
                helperText={newRowFormValidation.errors.date}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Payee"
                size="small"
                placeholder="Type payee name..."
                value={newRowFormValidation.data.payee || ''}
                onChange={(e) => newRowFormValidation.updateField('payee', e.target.value)}
                error={newRowFormValidation.hasFieldError('payee')}
                helperText={newRowFormValidation.errors.payee}
              />
              <AmountInputCell
                fullRow
                singleAmount={budget?.type === 2}
                debitValue={newRowFormValidation.data.debit}
                creditValue={newRowFormValidation.data.credit}
                onDebitChange={(val) => {
                  newRowFormValidation.updateField('debit', val);
                  if (val) newRowFormValidation.updateField('credit', '');
                }}
                onCreditChange={(val) => {
                  newRowFormValidation.updateField('credit', val);
                  if (val) newRowFormValidation.updateField('debit', '');
                }}
              />
              <Button
                fullWidth
                variant="contained"
                color="primary"
                disabled={savingNewRow}
                onClick={async () => {
                  const ok = await handleSaveNewRow();
                  if (ok) setAddSheetOpen(false);
                }}
              >
                {savingNewRow ? 'Saving...' : 'Save entry'}
              </Button>
            </Box>
          </Sheet>
        </>
      )}

      <HorizonSwitchDialog
        budget={horizonDialogOpen ? budget : null}
        onClose={() => setHorizonDialogOpen(false)}
        onSwitched={(updated) => {
          setHorizonDialogOpen(false);
          setBudget(updated);
          invalidateListCache(BUDGETS_LIST_CACHE_KEY);
        }}
      />

      {/* Delete Line Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Delete transaction?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this budget line?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BudgetDetail;